import moengage from '@moengage/web-sdk';
import { Utils } from '../utils';

export const MoEngageCardsEvents = Object.freeze({
    INBOX_OPENED: 'inboxOpened',
    CARD_SHOWN: 'cardShown',
});

const ensureMoEngageEnabled = (callback) => {
    if (process.env.REACT_APP_MOENGAGE_ENABLED === 'YES') {
        callback();
    }
};

/// Normalizes the HTML to Android format by converting inline color styles to font tags.
/// iOS cards html use <span> tags with inline color styles, while Android cards use <font> tags.
/// Android cards html is the reference format.
export const normalizeToAndroidFormat = (html) => {
    // Create a DOM parser to parse the HTML into a DOM document.
    const parser = new DOMParser();
    const document = parser.parseFromString(html, 'text/html');
  
    // Find all span elements with inline color styles.
    document.querySelectorAll('span[style]').forEach(span => {
      const style = span.getAttribute('style');
      if (style && style.includes('color')) {
        // Extract the rgb color using regex.
        const match = /rgb\((\d+),\s*(\d+),\s*(\d+)\)/.exec(style);
  
        if (match) {
          const r = parseInt(match[1]);
          const g = parseInt(match[2]);
          const b = parseInt(match[3]);
  
          // Ensure that RGB values are valid integers.
          if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
            // Convert to hex color.
            const hexColor = Utils.rgbToHex(r, g, b);
  
            // Create <font color="..."> element and replace the span with it.
            const fontElement = document.createElement('font');
            fontElement.setAttribute('color', hexColor);
            fontElement.innerHTML = span.innerHTML;
  
            span.replaceWith(fontElement);
          }
        }
      }
    });
  
    // Return the modified HTML string.
    return document.body ? document.body.innerHTML : html;
  }

const MoEngageWrapper = {
    initialize: () => {
        ensureMoEngageEnabled(() => {
            moengage.initialize({
                app_id: process.env.REACT_APP_MOENGAGE_WORKSPACE_ID,
                cluster: process.env.REACT_APP_MOENGAGE_DATACENTER,
                debug_logs: 0, // Force usage of MoEngage LIVE environment
                enableSPA: true,
                cards: {
                    enable: true,
                }
            });

            moengage.on_cards_loaded().then(function () {

            }).catch(function (error) {
                console.error('Failed to load MoEngage cards module:', error);
            });
        });
    },

    login: async (userTraceId, merchantTraceId) => {
        ensureMoEngageEnabled(async () => {
            try {
                const userIdentifier = `${userTraceId}@${merchantTraceId}`;
                const result = await moengage.add_unique_user_id(userIdentifier);
                if (result !== true) {
                    console.error(`Failed to set user for MoEngage.`, result);
                } 
            } catch (error) {
                console.error(`Failed to set user for MoEngage.`, error);
            }
        });
    },

    logout: async () => {
        ensureMoEngageEnabled(async () => {
            try {
                const result = await moengage.destroy_session();
                if (result !== true) {
                    console.error(`Failed to logout from MoEngage.`, result);
                } 
            } catch (error) {
                console.error(`Failed to logout from MoEngage.`, error);
            }
        });
    },

    trackEvent: async (eventName, eventData) => {
        ensureMoEngageEnabled(async () => {
            try {
                return await moengage.track_event(eventName, eventData);
            } catch (error) {
                console.error('Failed to track event on MoEngage:', error);
            }
        });
    },

    cards: {
        trackEvent: (event, eventData) => {
            ensureMoEngageEnabled(async () => {
                try {
                    switch (event) {
                        case MoEngageCardsEvents.INBOX_OPENED:
                            moengage.cards?.inboxOpened();
                            break;
                        case MoEngageCardsEvents.CARD_SHOWN:
                            moengage.cards?.cardShown(eventData + '_web');
                            break;
                        default:
                            console.error('Unknown MoEngage card event:', event);
                    }
                } catch (error) {
                    console.error('Failed to track card event on MoEngage:', error);
                }
            });
        },

        delete: (cardId) => {
            ensureMoEngageEnabled(async () => {
                try {
                    moengage.cards?.deleteCard(cardId + '_web');
                } catch (error) {
                    console.error('Failed to delete card:', error);
                }
            });
        },
    },
};

export default MoEngageWrapper;